import React, { useEffect, useState } from "react";
import { motion as Motion } from "framer-motion";
import { NavLink, Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Button from "./Button";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const navLinks = [
    { name: "HOME", path: "/" },
    { name: "ABOUT US", path: "/about" },
    { name: "PROGRAMS", path: "/programs" },
    { name: "PRICING", path: "/pricing" },
    { name: "GALLERY", path: "/gallery" },
    { name: "CONTACT", path: "/contact" },
  ];

  return (
    <Motion.nav
      initial={{ y: -8, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 w-full bg-[#7D192A] lg:bg-white z-[100] transform-gpu border-b border-black/5 transition-all duration-300 ${
        scrolled ? "py-2 shadow-soft" : "py-2 lg:py-3 shadow-sm"
      }`}
    >
      <div className="w-full flex items-center justify-between site-gutter">
        {/* Logo Section */}
        <div className="flex-shrink-0">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="bg-white p-1 rounded-full shadow-sm lg:bg-transparent lg:shadow-none lg:p-0 transition-transform duration-300 group-hover:scale-105">
              <img
                src="/assets/rfa-logo-sm.webp"
                width={56}
                height={56}
                alt="Reflection Futbol Academy logo"
                className={`object-contain transition-all duration-300 ${
                  scrolled
                    ? "h-8 w-8 lg:h-10 lg:w-10"
                    : "h-10 w-10 lg:h-12 lg:w-12"
                }`}
              />
            </div>
            <div className="font-extrabold text-[12px] lg:text-sm block leading-none sm:block text-white lg:text-[#7D192A] font-inter transition-colors">
              <span className="block leading-none">REFLECTION</span>
              <span className="block leading-none">FUTBOL</span>
              <span>ACADEMY</span>
            </div>
          </Link>
        </div>

        {/* 2. DESKTOP NAV */}
        <div className="hidden xl:flex items-center gap-3 2xl:gap-4">
          {navLinks.map((link) => (
            <NavLink
              key={link.name}
              to={link.path}
              className={({ isActive }) => `
                relative nav-text whitespace-nowrap pb-1 transition-all duration-300 hover:-translate-y-0.5
                after:content-[''] after:absolute after:left-1/2 after:-translate-x-1/2 after:bottom-0 after:h-[3px] after:bg-[#7D192A] after:transition-all after:duration-300
                ${
                  isActive
                    ? "text-[#7D192A] after:w-full"
                    : "text-[#111111] hover:text-[#7D192A] after:w-0 hover:after:w-full"
                }
              `}
            >
              {link.name}
            </NavLink>
          ))}

          <div className="flex items-center gap-2 ml-1">
            <Button
              to="/apply"
              variant="primary"
              pill
              className="px-3.5 py-2 text-xs font-bold whitespace-nowrap"
            >
              APPLY NOW
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              to="/login"
              variant="secondary"
              pill
              className="px-3.5 py-2 text-xs font-bold whitespace-nowrap"
            >
              MEMBER LOGIN
            </Button>
          </div>
        </div>

        {/* 3. MOBILE MENU BUTTON */}
        <div className="xl:hidden">
          <button
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Close menu" : "Open menu"}
            className="text-white lg:text-[#7D192A] p-3 min-h-11 min-w-11 flex items-center justify-center hover:bg-white/20 lg:hover:bg-[#7D192A]/10 rounded-lg transition-colors"
          >
            {isOpen ? (
              <X
                size={28}
                className="rotate-90 transition-transform duration-300"
              />
            ) : (
              <Menu size={28} className="transition-transform duration-300" />
            )}
          </button>
        </div>
      </div>

      {/* 4. MOBILE DROPDOWN MENU*/}
      <div
        className={`xl:hidden absolute top-full left-0 w-full bg-[#f5f5f5] shadow-xl transition-all duration-500 ease-in-out overflow-hidden z-[90] ${
          isOpen
            ? "max-h-[600px] opacity-100 py-6 border-t border-gray-200"
            : "max-h-0 opacity-0 py-0"
        }`}
      >
        <div className="px-6 flex flex-col items-center space-y-4">
          {/* Mobile Links */}
          {navLinks.map((link, index) => (
            <NavLink
              key={link.name}
              to={link.path}
              onClick={() => setIsOpen(false)}
              style={{ transitionDelay: `${index * 75}ms` }}
              className={({ isActive }) => `
                nav-text text-base lg:text-lg whitespace-nowrap transition-all duration-300 transform
                ${isOpen ? "translate-x-0 opacity-100" : "-translate-x-8 opacity-0"}
                ${
                  isActive
                    ? "text-[#7D192A] scale-110"
                    : "text-[#111111] hover:text-[#7D192A]"
                }
              `}
            >
              {link.name}
            </NavLink>
          ))}

          <div
            className={`w-full flex flex-col items-center gap-3 mt-4 transition-all duration-500 delay-300 ${
              isOpen ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            }`}
            onClick={() => setIsOpen(false)}
          >
            <div className="w-[200px]">
              <Button
                to="/apply"
                variant="primary"
                pill
                className="w-full flex justify-center min-h-11 py-3 text-xs font-bold whitespace-nowrap"
              >
                APPLY NOW
              </Button>
            </div>

            <div className="w-[200px]">
              <Button
                to="/login"
                variant="secondary"
                pill
                className="w-full flex justify-center min-h-11 py-3 text-xs font-bold whitespace-nowrap"
              >
                MEMBER LOGIN
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Motion.nav>
  );
};

export default Navbar;
